import {
  useState,
  // useEffect,
  useId,
  useRef,
  useLayoutEffect,
  type FocusEvent,
} from "react";
import cx from "classnames";

import { useComboboxLegacy } from "./ComboboxLegacy";

import { Flex } from "../Flex";
import { usePopoverLegacyContext } from "../PopoverLegacy";
import { Label, LabelProps } from "../Label";
import { TextFieldProps } from "../TextField";
import { Button } from "../Button";
import { Chip } from "../Chip";
import { Helper } from "../../internal/components";

import styles from "./ComboboxLegacy.module.scss";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Chevron_Right from "@servicetitan/hammer-icon/mdi/round/chevron_right.svg";

type ComboboxLegacyTriggerProps = Omit<
  TextFieldProps,
  "disabled" | "readOnly"
> &
  Pick<LabelProps, "required" | "moreInfo" | "openMoreInfo"> & {
    /**
     * Should be a positive integer.
     */
    maxRows?: number;
  } & {
    /** @deprecated use disabled in <ComboboxLegacy /> */
    disabled?: boolean;
    /** @deprecated use readOnly in <ComboboxLegacy /> */
    readOnly?: boolean;
  };

export type ComboboxLegacySearchFieldProps = ComboboxLegacyTriggerProps;
export type ComboboxLegacySelectTriggerProps = ComboboxLegacyTriggerProps;

export const ComboboxLegacyTrigger = ({
  // variant: variantProp = "combobox",
  className,
  label,
  size,
  error,
  hint,
  description,
  required,
  moreInfo,
  openMoreInfo,
  prefix,
  suffix,
  maxRows,
  id,
  disabled,
  readOnly,
  ...rest
}: ComboboxLegacyTriggerProps & { variant?: "combobox" | "select" }) => {
  const {
    select,
    disableClearSelection,
    itemToString,
    getLabelProps,
    getInputProps,
    getToggleButtonProps,
    selectedItem,
    selectedItems,
    getSelectedItemProps,
    getDropdownProps,
    reset,
    removeSelectedItem,
    selectItem,
    inputValue,
    isDisabled,
    isReadOnly,
    referenceRef,
    contentRef,
    closeMenu,
    setIsDisabled,
    setIsReadOnly,
  } = useComboboxLegacy();

  const variant = select ? "select" : "combobox";

  // TODO: Figure out how to fire this console.warn only in "development"
  // useEffect(() => {
  //   if (variant !== variantProp) {
  //     if (variant === "select") {
  //       console.warn(
  //         "ComboboxLegacy.SearchField should be used with non-Select ComboboxLegacy",
  //       );
  //     }

  //     if (variant === "combobox") {
  //       console.warn(
  //         "ComboboxLegacy.SelectTrigger should be used with ComboboxLegacy.Select",
  //       );
  //     }
  //   }
  // }, [variant, variantProp]);

  const context = usePopoverLegacyContext();

  const inputRef = useRef<HTMLInputElement>(null);

  const ComboboxLegacyTriggerClassNames = cx(styles["search-field"], {
    [styles["select"]]: variant === "select",
  });

  const noClearButton = isDisabled || isReadOnly || disableClearSelection;

  const ComboboxLegacyInputWrapperClassNames = cx(
    styles["input-wrapper"],
    className
      ? {
          [className]: variant === "select",
        }
      : {},
    {
      [styles["small"]]: size === "small",
      [styles["large"]]: size === "large",
      [styles["no-clear-button"]]: noClearButton,
    },
  );

  const ComboboxLegacyInputClassNames = cx(
    styles["input"],
    className
      ? {
          [className]: variant === "combobox",
        }
      : {},
    {
      [styles["error"]]: error,
    },
  );

  useLayoutEffect(() => {
    if (typeof disabled === "undefined") return;
    setIsDisabled(disabled);
  }, [disabled, setIsDisabled]);

  useLayoutEffect(() => {
    if (typeof readOnly === "undefined") return;
    setIsReadOnly(readOnly);
  }, [readOnly, setIsReadOnly]);

  const labelUid = "label" + useId();

  const helperUid = "helper" + useId();
  const placeholderUid = "placeholder" + useId();
  const ariaDescribedBy = `${helperUid} ${placeholderUid}`;

  const errorMessage = typeof error !== "boolean" ? error : undefined;

  // if we focus out to something not within the reference or content divs
  // then close the popover
  const handleBlur = (e: FocusEvent) => {
    const target = e.relatedTarget ?? e.target;
    const focusedOnReference =
      referenceRef.current && referenceRef.current.contains(target);
    const focusedOnContent =
      contentRef.current && contentRef.current.contains(target);

    if (!focusedOnReference && !focusedOnContent) {
      closeMenu();
    }
  };

  const rowsRef = useRef<HTMLDivElement>(null);
  const [stillCalculating, setStillCalculating] = useState<boolean>(false);
  const [collapsedChips, setCollapsedChips] = useState<boolean>(false);
  const [visibleChipsCount, setVisibleChipsCount] = useState<number | null>(
    null,
  );
  const [forceRenderCount, setForceRenderCount] = useState<number>(0);

  useLayoutEffect(() => {
    // we only need to do this nonsense if maxRows is set
    if (maxRows === undefined) return;

    // 1) Render with no chips collapsed, if it's already just one row, we're done
    // 2) If there is a second row, then render again with best guess at how we should collapse
    // 3) If there is still a second row while collapsed, then render again with one less item
    // 4) Keep going until there is only one row, then /actually/ render

    // start calculation loop, set intiial values and force re-render
    if (stillCalculating === false) {
      setStillCalculating(true);
      setCollapsedChips(false);
      setVisibleChipsCount(null);
      setForceRenderCount((x) => x + 1);

      // otherwise, calculate the number of rows
    } else if (rowsRef?.current?.children) {
      const children = rowsRef?.current?.children;

      const rowData = Array.from(children).reduce(
        (acc, child) => {
          const top = child.getBoundingClientRect().top;

          if (!acc.length) {
            return [{ count: 1, top }];
          } else if (acc[acc.length - 1].top === top) {
            return [
              ...acc.slice(0, -1),
              { count: acc[acc.length - 1].count + 1, top },
            ];
          } else if (acc[acc.length - 1].top !== top) {
            return [...acc, { count: 1, top }];
          } else {
            return acc;
          }
        },
        [] as { count: number; top: number }[],
      );

      // if we don't have a second row or if we're already hiding everything, call it a day
      if (!rowData[maxRows] || visibleChipsCount === 0) {
        setStillCalculating(false);
      } else {
        setCollapsedChips(true);

        // otherwise, either guess how many visible chips we have
        if (visibleChipsCount == null) {
          const firstGuess = Math.max(
            rowData.reduce((sum, row) => sum + row.count, -2),
            0,
          );
          setVisibleChipsCount(firstGuess);

          // or decrease our last guess by 1 and try again
        } else {
          setVisibleChipsCount(visibleChipsCount - 1);
        }

        setForceRenderCount((x) => x + 1);
      }
    }
  }, [selectedItems.length, maxRows, forceRenderCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedItemsDisplayCount =
    collapsedChips && maxRows != null && visibleChipsCount !== null
      ? visibleChipsCount
      : selectedItems.length;

  // TODO: Update this to use a TextField component once we can pass props
  //       directly to both the label and the text input
  return (
    <div
      className={ComboboxLegacyTriggerClassNames}
      data-anv="combobox-searchfield"
      ref={referenceRef}
      onBlur={handleBlur}
    >
      {label ? (
        <Label
          {...getLabelProps({
            className: styles["label"],
            required,
            moreInfo,
            openMoreInfo,
            id: labelUid,
          })}
        >
          {label}
        </Label>
      ) : null}

      <div
        className={ComboboxLegacyInputWrapperClassNames}
        role="presentation"
        {...(variant === "select"
          ? {
              ...getToggleButtonProps({
                disabled: isDisabled,
                readOnly: isReadOnly,
                ref: context.refs.setReference,
                id,
                "aria-describedby": ariaDescribedBy,
                "aria-labelledby": labelUid,
              }),
            }
          : {
              ref: context.refs.setReference,
              onClick: () => inputRef.current?.focus(),
            })}
      >
        <div className={styles["buttons-wrapper"]}>
          {(inputValue || selectedItem || selectedItems.length) &&
          !noClearButton ? (
            <div className={styles["close-button-wrapper"]}>
              <Button
                aria-label="clear selection"
                appearance="ghost"
                size="small"
                icon={Close}
                className={styles["close-button"]}
                onClick={(e) => {
                  e.stopPropagation();

                  if (reset != null) {
                    reset();
                  } else {
                    selectItem(null);
                  }
                }}
                tabIndex={-1}
              />
            </div>
          ) : null}

          <div className={styles["toggle-button-wrapper"]}>
            <Button
              {...(variant === "select"
                ? { tabIndex: -1 }
                : getToggleButtonProps())}
              className={styles["toggle-button"]}
              aria-label="toggle menu"
              icon={Chevron_Right}
              appearance="ghost"
              size="small"
              disabled={isDisabled}
            />
          </div>
        </div>

        <div className={styles["rows-wrapper"]} ref={rowsRef}>
          {selectedItems.length &&
          removeSelectedItem != null &&
          getSelectedItemProps != null
            ? selectedItems
                .slice(0, selectedItemsDisplayCount)
                .map((item, index) => {
                  const { onClick, ...selectedItemProps } =
                    getSelectedItemProps({
                      selectedItem: item,
                      index,
                    });

                  return (
                    <div
                      key={`selected-item-${index}`}
                      className={styles["chip-wrapper"]}
                    >
                      <Chip
                        {...selectedItemProps}
                        label={itemToString(item)}
                        onClose={
                          isDisabled || isReadOnly
                            ? undefined
                            : (_e) => {
                                removeSelectedItem(item);
                              }
                        }
                        className={styles["chip"]}
                        title={itemToString(item)}
                      />
                    </div>
                  );
                })
            : null}

          {collapsedChips && maxRows != null ? (
            <div className={styles["chip-wrapper"]}>
              <Chip
                label={`+${selectedItems.length - selectedItemsDisplayCount}`}
                className={styles["chip"]}
                title={selectedItems
                  .slice(selectedItemsDisplayCount)
                  .map((item) => itemToString(item))
                  .join(", ")}
              />
            </div>
          ) : null}

          <Flex className={styles["input-flex"]} alignItems="center">
            {prefix ? <div className={styles["prefix"]}>{prefix}</div> : null}

            {variant === "select" ? (
              <div
                {...(getDropdownProps != null
                  ? getDropdownProps({
                      className: ComboboxLegacyInputClassNames,
                      required,
                      preventKeyAction: false,
                    })
                  : {
                      className: ComboboxLegacyInputClassNames,
                      required,
                    })}
              >
                {selectedItems.length ? null : selectedItem ? (
                  itemToString(selectedItem)
                ) : (
                  <span
                    className={styles["fake-placeholder"]}
                    id={placeholderUid}
                  >
                    {rest.placeholder}
                  </span>
                )}
              </div>
            ) : (
              <input
                {...(getDropdownProps != null && getInputProps != null
                  ? getInputProps(
                      getDropdownProps({
                        ...rest,
                        className: ComboboxLegacyInputClassNames,
                        disabled: isDisabled,
                        readOnly: isReadOnly,
                        required,
                        autoComplete: "off",
                        id,
                        "aria-describedby": ariaDescribedBy,
                        "aria-labelledby": labelUid,
                        preventKeyAction: false,
                        ref: inputRef,
                      }),
                    )
                  : getInputProps != null
                    ? getInputProps({
                        ...rest,
                        className: ComboboxLegacyInputClassNames,
                        disabled: isDisabled,
                        readOnly: isReadOnly,
                        required,
                        autoComplete: "off",
                        id,
                        "aria-describedby": ariaDescribedBy,
                        "aria-labelledby": labelUid,
                        ref: inputRef,
                      })
                    : {})}
              />
            )}

            {suffix ? <div className={styles["suffix"]}>{suffix}</div> : null}
          </Flex>
        </div>
      </div>

      {hint || errorMessage || description ? (
        <Helper
          id={helperUid}
          errorMessage={errorMessage}
          hint={hint}
          description={description}
        />
      ) : null}
    </div>
  );
};

export const ComboboxLegacySearchField = (
  props: ComboboxLegacyTriggerProps,
) => {
  return <ComboboxLegacyTrigger {...props} variant="combobox" />;
};

export const ComboboxLegacySelectTrigger = (
  props: ComboboxLegacyTriggerProps,
) => {
  return <ComboboxLegacyTrigger {...props} variant="select" />;
};
